import { useEffect, useState } from 'react'
import { useParams } from 'react-router'

import { Table } from 'common/components/Table/Table'
import { DEFAULT_TABLE_PARAMS_WITH_SEARCH } from 'common/constants/defaultTableParamsConstants'

import type { IBooking } from 'features/Booking'
import { useLazyFetchBookingsWithPaginationQuery } from 'features/Booking/state/api/bookingApi'
import type { ObjectType } from 'common/interfaces/object'
import type { TableParamsType } from 'common/interfaces/ITable'
import { TableSearchAndFilters } from 'common/components/TableSearchAndFilters/TableSearchAndFilters'
import { BOOKING_TYPE } from 'features/Booking/constants/bookingType'

import { completedBookingsColumns } from './completedBookingsColumns'
import { UnavailableLeaseModal } from './UnavailableLeaseModal'

const CompletedBookings = () => {
  const [columns, setColumns] = useState<ObjectType[]>([])
  const [unavailableBookingId, setUnavailableBookingId] = useState<string | null>(null)
  const [tableParams, setTableParams] = useState<TableParamsType>({
    ...DEFAULT_TABLE_PARAMS_WITH_SEARCH,
  })

  const { siteId } = useParams()

  const [fetchCompletedLeases, { data, isFetching }] = useLazyFetchBookingsWithPaginationQuery()

  useEffect(() => {
    if (!siteId) {
      return
    }

    setColumns(
      completedBookingsColumns({
        siteId,
        onUnavailableLease: (booking: IBooking) => setUnavailableBookingId(booking.id),
      }),
    )
  }, [siteId])

  useEffect(() => {
    if (data?.total_items >= 0) {
      setTableParams({ ...tableParams, total: data.total_items })
    }
  }, [data])

  useEffect(() => {
    if (tableParams.sorters) {
      setColumns((prev) =>
        prev.map((column) => ({ ...column, sortOrder: tableParams.sorters?.[column.key]?.order })),
      )
    }
  }, [tableParams.sorters])

  useEffect(() => {
    if (siteId) {
      fetchCompletedLeases({
        type: BOOKING_TYPE.COMPLETED,
        siteId,
        tableParams: {
          ...tableParams,
          search: tableParams.debouncedValue?.length >= 2 ? tableParams.debouncedValue : '',
        },
      })
    }
  }, [
    siteId,
    tableParams.pageSize,
    tableParams.current,
    tableParams.debouncedValue,
    tableParams.sorters,
  ])

  return (
    <>
      <h1 className='page-title'>Completed Bookings</h1>
      <TableSearchAndFilters
        setTableParams={setTableParams}
        setColumns={setColumns}
        tableParams={tableParams}
        inputHint='Search by booking number, booking type, status'
      />
      <Table
        itemKey='id'
        haveActions={false}
        dataSource={data?.items}
        columns={columns}
        isLoading={isFetching}
        tableParams={tableParams}
        setTableParams={setTableParams}
      />
      <UnavailableLeaseModal
        bookingId={unavailableBookingId ?? ''}
        isOpen={!!unavailableBookingId}
        onClose={() => setUnavailableBookingId(null)}
      />
    </>
  )
}

export default CompletedBookings
